import type { AudioBus, Vec3Like, Voice } from './audioBus';
import { jitter, noise, pick, tone } from './synth';

/**
 * Sounds of the table: gems tapped on the tablets, pieces picked up, pieces crossing the board
 * each in their own way, and the drums that announce a capture. Everything is at table scale –
 * small wooden figures on a board – so the footfalls are taps rather than thuds.
 */
export interface SustainedSound {
  place(at: Vec3Like): void;
  stop(): void;
}

/** A gem on a tablet was tapped; a dull knock when it could not be used. */
export function gemTap(bus: AudioBus, enabled = true): void {
  if (!enabled) {
    const v = bus.voice('gem-dull', { priority: 'low', gain: 0.4 });
    if (!v) return;
    v.end(
      tone(v, {
        type: 'triangle',
        freq: 210,
        sweep: { to: 150 },
        seconds: 0.05,
        env: { attack: 0.004, release: 0.08 },
        gain: 0.6,
      }),
    );
    return;
  }
  const v = bus.voice('gem-tap', { priority: 'low', gain: 0.45 });
  if (!v) return;
  tone(v, {
    freq: 1900,
    sweep: { to: 1150, seconds: 0.05 },
    seconds: 0.03,
    env: { attack: 0.002, release: 0.06 },
    gain: 0.5,
  });
  v.end(
    noise(v, {
      seconds: 0.008,
      env: { attack: 0.001, release: 0.02 },
      filter: { type: 'highpass', freq: 3500 },
      gain: 0.35,
    }),
  );
}

/** A piece was picked: a bright two-note ping at the piece. */
export function pieceSelected(bus: AudioBus, at?: Vec3Like): void {
  const v = bus.voice('piece-select', { at, priority: 'low', gain: 0.4, reverb: 0.3 });
  if (!v) return;
  tone(v, { freq: 660, seconds: 0.06, env: { attack: 0.004, release: 0.12 }, gain: 0.5 });
  v.end(
    tone(v, {
      freq: 990,
      seconds: 0.08,
      at: 0.07,
      env: { attack: 0.004, release: 0.18 },
      gain: 0.45,
    }),
  );
}

/**
 * One footfall of a piece on the board – the tap of a small wooden figure. `weight` 0 … 1 makes
 * it lower, longer and louder; alternate steps differ a shade so a walk does not tick.
 */
export function boardFootfall(bus: AudioBus, at: Vec3Like, weight: number, step: number): void {
  const v = bus.voice('footfall', { at, priority: 'low', gain: 0.3 + 0.5 * weight });
  if (!v) return;
  const knock = jitter(1300 - 900 * weight, 0.1) * (step % 2 ? 1 : 0.93);
  tone(v, {
    freq: knock,
    sweep: { to: knock * 0.55, seconds: 0.04 },
    seconds: 0.015 + 0.04 * weight,
    env: { attack: 0.002, release: 0.04 + 0.1 * weight },
    gain: 0.6,
  });
  v.end(
    noise(v, {
      seconds: 0.008 + 0.02 * weight,
      env: { attack: 0.001, release: 0.03 },
      filter: { type: 'bandpass', freq: 2600 - 1500 * weight, q: 1.2 },
      gain: 0.45,
    }),
  );
}

/** A wing beat: a puff of air, deeper and slower for a big flier (`size` 0 … 1). */
export function wingBeat(bus: AudioBus, at: Vec3Like | undefined, size: number): void {
  const v = bus.voice('wing', { at, priority: 'low', gain: 0.5 });
  if (!v) return;
  const seconds = jitter(0.14 + 0.12 * size, 0.15);
  v.end(
    noise(v, {
      kind: 'pink',
      seconds,
      env: { attack: seconds * 0.45, release: seconds * 0.6 },
      filter: { type: 'bandpass', freq: 900 - 500 * size, sweep: { to: 220 }, q: 0.9 },
      gain: 0.8,
    }),
  );
}

/** The airy hum of a floating piece; runs until stopped. */
export function floatHum(bus: AudioBus, at: Vec3Like): SustainedSound | null {
  const v = bus.voice('float-hum', { at, priority: 'low', gain: 0.35, reverb: 0.3 });
  if (!v) return null;
  // The caller stops it when the piece settles; the length is only a safety net.
  const seconds = 20;
  const base = jitter(150, 0.1);
  tone(v, {
    freq: base,
    seconds,
    env: { attack: 0.25, release: 0.35 },
    gain: 0.45,
    vibrato: { hz: 5, cents: 15 },
    am: { hz: 4.3, depth: 0.3 },
  });
  tone(v, {
    type: 'triangle',
    freq: base * 1.503,
    seconds,
    env: { attack: 0.4, release: 0.35 },
    gain: 0.22,
    am: { hz: 3.1, depth: 0.45 },
  });
  noise(v, {
    kind: 'pink',
    seconds,
    env: { attack: 0.5, release: 0.35 },
    filter: { type: 'bandpass', freq: 600, sweep: { to: 1500, seconds: 4 }, q: 2.5 },
    gain: 0.3,
  });
  v.end(v.now + seconds + 0.4);
  return { place: (p) => v.place(p), stop: () => v.stop(0.35) };
}

/** A piece blinks out of its square, or in on the next: a shimmer down, or up with a sparkle. */
export function blinkShimmer(bus: AudioBus, at: Vec3Like, direction: 'out' | 'in'): void {
  const v = bus.voice(`blink-${direction}`, { at, gain: 0.4, reverb: 0.45 });
  if (!v) return;
  const up = direction === 'in';
  let end = tone(v, {
    freq: up ? 520 : 1900,
    sweep: { to: up ? 1900 : 520, seconds: 0.24 },
    seconds: 0.2,
    env: { attack: 0.02, release: 0.16 },
    gain: 0.45,
    fm: { ratio: 3.01, depth: 320, depthTo: up ? 40 : 500 },
  });
  end = Math.max(
    end,
    noise(v, {
      seconds: 0.2,
      env: { attack: up ? 0.16 : 0.01, release: up ? 0.04 : 0.22 },
      filter: { type: 'highpass', freq: 4500 },
      gain: 0.14,
    }),
  );
  if (up)
    for (let i = 0; i < 3; i++)
      end = Math.max(
        end,
        tone(v, {
          freq: pick([2637, 3136, 3520, 4186]),
          seconds: 0.04,
          at: 0.14 + i * 0.05,
          env: { attack: 0.004, release: 0.14 },
          gain: 0.18,
        }),
      );
  v.end(end);
}

/** A capture begins: war drums, and a crash on the last beat. */
export function captureDrums(bus: AudioBus): void {
  const v = bus.voice('capture-drums', { priority: 'high', gain: 0.8, reverb: 0.25 });
  if (!v) return;
  const beats = [0, 0.19, 0.38, 0.57, 0.86];
  let end = 0;
  beats.forEach((at, i) => {
    end = Math.max(end, drum(v, at, i === beats.length - 1 ? 1 : 0.65 + 0.08 * i));
  });
  end = Math.max(
    end,
    noise(v, {
      seconds: 0.08,
      at: 0.86,
      env: { attack: 0.003, release: 0.7 },
      filter: { type: 'highpass', freq: 4500 },
      gain: 0.35,
    }),
  );
  v.end(end);
}

function drum(v: Voice, at: number, level: number): number {
  tone(v, {
    freq: 170,
    sweep: { to: 48, seconds: 0.09 },
    seconds: 0.05,
    at,
    env: { attack: 0.003, release: 0.28 },
    gain: 0.9 * level,
  });
  return noise(v, {
    seconds: 0.02,
    at,
    env: { attack: 0.002, release: 0.06 },
    filter: { type: 'lowpass', freq: 900 },
    gain: 0.5 * level,
  });
}

/** A new game is set up: pieces knocked onto the board, then a rising chime. */
export function newGame(bus: AudioBus): void {
  const v = bus.voice('new-game', { gain: 0.5, reverb: 0.3 });
  if (!v) return;
  let end = 0;
  for (let i = 0; i < 3; i++)
    end = Math.max(
      end,
      tone(v, {
        freq: jitter(700, 0.15),
        sweep: { to: 400, seconds: 0.04 },
        seconds: 0.02,
        at: i * 0.09,
        env: { attack: 0.002, release: 0.08 },
        gain: 0.4,
      }),
    );
  end = Math.max(
    end,
    tone(v, {
      freq: 784,
      seconds: 0.12,
      at: 0.34,
      env: { attack: 0.01, release: 0.3 },
      gain: 0.35,
    }),
  );
  end = Math.max(
    end,
    tone(v, {
      freq: 1175,
      seconds: 0.2,
      at: 0.46,
      env: { attack: 0.01, release: 0.5 },
      gain: 0.35,
    }),
  );
  v.end(end);
}
